import { useCallback } from 'react';

import { trackRadarOrderPlaced } from '@sg/radar-fe';

import { prepareLedger } from '@order/features-ordering';
import { type OrderingContext } from '@order/features-ordering';
import { useAdjustEvents } from '@order/integrations-adjust';
import { useAppsFlyer } from '@order/integrations-appsflyer';
import {
  formatBagForAnalytics,
  formatCustomerPersonalInfo,
  googleAnalytics,
} from '@order/Telemetry';
import { convertPriceToFloat } from '@order/utils';

import { useSnapPixel } from '../../../../integrations';

export const usePostOrderTracking = () => {
  const { logPurchaseEvent: trackAppsFlyerPurchase } = useAppsFlyer();
  const { trackAdjustPurchase } = useAdjustEvents();
  const { trackPurchase: trackSnapPixelPurchase } = useSnapPixel();

  const handlePostOrderTracking = useCallback(
    ({ context }: { context: OrderingContext }) => {
      const {
        orderId = '',
        cart,
        selectedTip,
        availableCredit,
        shouldUseCredit,
        amountEligibleForFlex,
        shouldUseFlex,
      } = context;

      const { lineItems, ledger, restaurantId = '' } = cart;

      const { subtotal } = prepareLedger({
        ledger,
        tip: selectedTip,
        availableCredit,
        shouldUseCredit,
        amountEligibleForFlex,
        shouldUseFlex,
      });

      const subtotalValue = subtotal / 100;

      trackSnapPixelPurchase({
        priceInDollars: subtotalValue,
        orderId,
        productIds: lineItems?.map((item) => item.product?.id ?? '') ?? [],
        lineItemsQuantity: lineItems?.length ?? 0,
      });

      const telemetryBag = formatTelemetryBag(context, subtotalValue);

      googleAnalytics.track({
        event: 'purchase',
        userId: context?.customer?.customerId,
        transaction_id: orderId,
        new_customer: context?.customer?.isNewCustomer,
        ...formatBagForAnalytics({ ...telemetryBag, orderValue: subtotalValue }),
        ...formatCustomerPersonalInfo(telemetryBag),
      });

      trackAppsFlyerPurchase({
        orderId,
        orderValue: subtotalValue,
        orderType: telemetryBag.orderType,
        bagCount: telemetryBag.bagCount,
        productIds: telemetryBag.productIds,
      });

      trackAdjustPurchase({ orderId, restaurantId, subtotalValue });

      const deliveryAddress =
        cart.orderChannel === 'delivery' ? cart.deliveryOrderDetail?.address : undefined;

      trackRadarOrderPlaced({
        orderId,
        orderType: telemetryBag.orderType,
        revenue: subtotalValue,
        restaurantSlug: cart.restaurantSlug ?? '',
        restaurantEntityId: cart.restaurantEntityId ?? '',
        restaurantName: cart.restaurantName ?? '',
        deliveryLocation: deliveryAddress
          ? {
              latitude: deliveryAddress.latitude,
              longitude: deliveryAddress.longitude,
              street: deliveryAddress.street,
              city: deliveryAddress.city,
              state: deliveryAddress.state,
              zipCode: deliveryAddress.zipCode,
            }
          : undefined,
      });
    },
    [trackAdjustPurchase, trackSnapPixelPurchase, trackAppsFlyerPurchase],
  );

  // ─────────────────────────────────────────────────────────────────────

  return { handlePostOrderTracking };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatTelemetryBag(context: OrderingContext, orderValue: number) {
  const products =
    context.cart?.lineItems?.map((item) => ({
      id: item.product?.id ?? '',
      name: item.product?.name ?? '',
      quantity: item.quantity ?? 0,
      cost: convertPriceToFloat(item.perItemCost),
    })) ?? [];

  return {
    orderValue,
    products,
    productIds: products.map((item) => item.id).join(','),
    bagCount: products.reduce((acc, item) => acc + (item.quantity ?? 0), 0),
    restaurantId: context?.cart?.restaurantId,
    restaurantName: context?.cart?.locationName,
    orderId: context?.orderId,
    orderType: context?.cart?.orderChannel,
    street: context?.cart?.deliveryOrderDetail?.address?.street,
    city: context?.cart?.deliveryOrderDetail?.address?.city,
    state: context?.cart?.deliveryOrderDetail?.address?.state,
    zipCode: context?.cart?.deliveryOrderDetail?.address?.zipCode,
    country: context?.cart?.deliveryOrderDetail?.address?.country,
    customer: {
      firstName: context?.customer?.firstName,
      lastName: context?.customer?.lastName,
      email: context?.customer?.email,
      phoneNumber: context?.customer?.phoneNumber,
    },
  };
}
